import { Link } from "react-router-dom";
import { ArrowLeft, RotateCcw, Trash2 } from "lucide-react";
import { useTrashedVMs, useRestoreVM, usePurgeVM } from "@/hooks/useVMs";
import { useAuth } from "@/auth/useAuth";
import { LoadingSpinner } from "@/components/common/LoadingSpinner";

const RETENTION_DAYS = 30;

function daysLeft(deletedAt: string): number {
  const deleted = new Date(deletedAt).getTime();
  const elapsed = (Date.now() - deleted) / 86_400_000;
  return Math.max(0, Math.ceil(RETENTION_DAYS - elapsed));
}

export default function TrashedVMs() {
  const { data: vms, isLoading } = useTrashedVMs();
  const { isAdmin } = useAuth();
  const restoreVM = useRestoreVM();
  const purgeVM = usePurgeVM();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-100">Trash</h1>
          <p className="mt-1 text-sm text-gray-400">
            Soft-deleted VMs are kept for {RETENTION_DAYS} days. Restore brings
            them back; purge destroys the Proxmox VM and the database row for
            real (admin only).
          </p>
        </div>
        <Link
          to="/vms"
          className="inline-flex items-center gap-2 rounded-lg border border-gray-700 px-4 py-2 text-sm font-medium text-gray-300 transition-colors hover:bg-gray-800"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to VMs
        </Link>
      </div>

      {isLoading ? (
        <LoadingSpinner />
      ) : !vms || vms.length === 0 ? (
        <div className="rounded-xl border border-gray-700 bg-gray-800 p-8 text-center text-sm text-gray-400">
          Trash is empty. Soft-deleted VMs will appear here.
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-700">
          <table className="min-w-full divide-y divide-gray-700 bg-gray-800">
            <thead className="bg-gray-900/50 text-xs uppercase text-gray-400">
              <tr>
                <th className="px-4 py-3 text-left">Name</th>
                <th className="px-4 py-3 text-left">VMID</th>
                <th className="px-4 py-3 text-left">Node</th>
                <th className="px-4 py-3 text-left">Tier</th>
                <th className="px-4 py-3 text-left">Deleted</th>
                <th className="px-4 py-3 text-left">Days left</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700 text-sm text-gray-200">
              {vms.map((vm) => (
                <tr key={vm.id} className="hover:bg-gray-700/30">
                  <td className="px-4 py-3 font-medium">{vm.name}</td>
                  <td className="px-4 py-3 font-mono">{vm.vmid}</td>
                  <td className="px-4 py-3">{vm.node}</td>
                  <td className="px-4 py-3">{vm.service_tier}</td>
                  <td className="px-4 py-3 text-gray-400">
                    {vm.deleted_at
                      ? new Date(vm.deleted_at).toLocaleString()
                      : "—"}
                  </td>
                  <td className="px-4 py-3">
                    {vm.deleted_at ? daysLeft(vm.deleted_at) : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => restoreVM.mutate(vm.id)}
                        disabled={restoreVM.isPending}
                        title="Restore"
                        className="rounded-lg p-2 text-green-400 transition-colors hover:bg-green-900/30 disabled:opacity-40"
                      >
                        <RotateCcw className="h-4 w-4" />
                      </button>
                      {isAdmin && (
                        <button
                          onClick={() => {
                            if (
                              confirm(
                                `PERMANENTLY destroy ${vm.name} (vmid ${vm.vmid})? This cannot be undone.`
                              )
                            ) {
                              purgeVM.mutate(vm.id);
                            }
                          }}
                          disabled={purgeVM.isPending}
                          title="Purge (hard delete)"
                          className="rounded-lg p-2 text-red-400 transition-colors hover:bg-red-900/30 disabled:opacity-40"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
